/**
 * Full SSR (treatment) entry for the artwork tag detail route.
 * Mounted via `dynamicWithExperiment` from
 * `routes/_app.tags.artwork.$tag.tsx` whenever the global
 * `SSR_DUAL_MODE_EXPERIMENT_NAME` experiment resolves to `treatment`.
 *
 * Verbatim move of the previous inline `default` Component:
 * - Builds a route-scoped Apollo client seeded with `GetTack` and
 *   `ListArtworks` payloads from the loader so `useQuery` /
 *   `useInfiniteListArtworks` hit cache on first render.
 * - Falls back to `TagPageShell` when the loader skipped the
 *   tagShell (typically when the tag was not found).
 * - Passes `initialTagSlug` / `initialDisplayName` / `initialTack` /
 *   `initialArtworkConnectionData` down to `TagPage` so the first
 *   paint includes real artworks, not skeletons.
 *
 * The CSR (control) entry lives in `_app.tags.artwork.$tag.csr.tsx`
 * and ignores all of these branches; it relies on the global
 * `RootRuntime` Apollo client.
 */
import { ApolloProvider } from '@apollo/client/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useLoaderData } from 'react-router'
import { TagPageShell } from 'src/components/Tag/TagPageShell'
import { TagPage } from 'src/pages/AppPage/TagPage'
import type { loader } from 'src/routes/_app.tags.artwork.$tag'
import {
  GetTackDocument,
  ListArtworksDocument,
} from 'src/services/apollo/generated/graphql'
import { createRouteApolloClient } from 'src/services/apollo/route-ssr-client'
import { useRequestContext } from 'src/utils/request-context'

export default function ArtworkTagFullSSREntry() {
  const loaderData = useLoaderData<typeof loader>()
  const { queryClient } = useRequestContext()

  const fallback = loaderData.tagShell ? (
    <TagPageShell shell={loaderData.tagShell} />
  ) : null

  const client = useMemo(() => {
    return createRouteApolloClient([
      ...(loaderData.artworkTagApollo.tackData
        ? [
            {
              query: GetTackDocument,
              variables: { codeName: loaderData.artworkTagApollo.tagSlug },
              data: loaderData.artworkTagApollo.tackData,
            },
          ]
        : []),
      ...(loaderData.artworkTagApollo.listArtworkData
        ? [
            {
              query: ListArtworksDocument,
              variables: loaderData.artworkTagApollo.listArtworkVariables,
              data: loaderData.artworkTagApollo.listArtworkData,
            },
          ]
        : []),
    ])
  }, [loaderData.artworkTagApollo])

  if (!loaderData.tagShell || !client) {
    return fallback
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={client}>
        <TagPage
          type="artwork"
          initialTagSlug={loaderData.artworkTagApollo.tagSlug}
          initialDisplayName={loaderData.tagShell.displayName}
          initialTack={loaderData.artworkTagApollo.tackData?.tack}
          initialArtworkConnectionData={loaderData.initialArtworkConnectionData}
        />
      </ApolloProvider>
    </QueryClientProvider>
  )
}
